import { useCallback, useRef } from 'react'

import { cn } from '@repo/utils'

import { trackEvent } from 'global-utils/Analytics'

import './ImageAd.css'

import type { CSSProperties, FC } from 'react'

interface ImageAdProps {
  alt?: string | null
  campaign: string
  className?: string | null
  client: string
  disable?: boolean
  hidden?: boolean
  image: string
  image2x?: string | null
  link?: string | null
  name: string
  noView?: boolean
  style?: CSSProperties
}

export const ImageAd: FC<ImageAdProps> = ({
  alt,
  campaign,
  className,
  client,
  disable = false,
  hidden = false,
  image,
  image2x,
  link,
  name,
  noView = false,
  style = {},
}) => {
  const viewedRef = useRef(false)

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  const isCompact = window.innerWidth <= 800

  const onClickImage = useCallback(() => {
    trackEvent(campaign, client, name)
  }, [client, campaign, name])

  const onLoadImage = useCallback(() => {
    if (!disable && !noView && !viewedRef.current) {
      trackEvent(campaign, client, name)
      viewedRef.current = true
    }
  }, [disable, noView, client, campaign, name])

  const src = isCompact || isMobile ? image : image2x || image
  const imageStyle: CSSProperties = {
    maxHeight: '100%',
    maxWidth: '100%',
    opacity: hidden ? 0 : 1,
  }

  const imageTag = !!src && (
    <img alt={hidden ? '' : alt || campaign} className="block" onLoad={onLoadImage} src={src} style={imageStyle} />
  )

  const isLocal = link?.includes('sweatbits.co') || link?.startsWith('/') || link?.includes('localhost:')

  if (link && !disable && !hidden) {
    return (
      <a
        className={cn('ImageAd', className, isCompact || isMobile ? 'mobile' : '')}
        href={link}
        onClick={onClickImage}
        rel={isLocal ? undefined : 'noreferrer'}
        style={style}
        target={isLocal ? undefined : link.startsWith('http') ? '_blank' : '_self'}
        title={alt || campaign}
      >
        {imageTag}
      </a>
    )
  }

  return (
    <span
      className={cn('ImageAd', className, isCompact || isMobile ? 'mobile' : '')}
      style={style}
      title={hidden ? '' : alt || campaign}
    >
      {imageTag}
    </span>
  )
}

export default ImageAd
